// Order Audit / Chain of Custody — admin/ops page to reconstruct any
// shipment dispute from stored records (timestamps, users, OTP verification,
// proof photos, audit log, line-haul AWB).
import { useState } from "react";
import api from "../../services/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Loader2, ShieldCheck, Search, CheckCircle2, Circle, KeyRound, Camera, Truck, ScrollText,
} from "lucide-react";

const ORANGE = "#FF6B00";

export default function ChainOfCustody() {
  const [tid, setTid] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async (id) => {
    const t = (id || tid).trim();
    if (!t) return;
    setLoading(true); setData(null);
    try {
      const { data } = await api.get(`/admin/orders/${t}/chain-of-custody`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Order not found");
    } finally { setLoading(false); }
  };

  const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="chain-of-custody-page">
      <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
        <ScrollText className="h-7 w-7" style={{ color: ORANGE }} /> Order Audit · Chain of Custody
      </h1>
      <p className="text-slate-500 mt-1 text-sm">Reconstruct any shipment from stored records — timestamps, users, OTP verification, proof photos, audit logs, line-haul.</p>

      <div className="flex gap-2 mt-5 mb-6">
        <Input placeholder="Enter Tracking ID (e.g. PKG-XXXXXXXX)" value={tid}
               onChange={(e) => setTid(e.target.value.toUpperCase())}
               onKeyDown={(e) => e.key === "Enter" && load()}
               className="rounded-xl font-mono" data-testid="coc-tid-input" />
        <Button onClick={() => load()} disabled={loading} style={{ background: ORANGE }} className="text-white rounded-xl" data-testid="coc-load-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" /> Load</>}
        </Button>
      </div>

      {data && (
        <div className="space-y-5" data-testid="coc-result">
          {/* Summary */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap gap-4 items-center justify-between">
            <div>
              <div className="font-mono font-bold text-lg text-slate-900">{data.tracking_id}</div>
              <div className="text-xs text-slate-500">Customer: {data.customer_name || "—"} · Status: <b>{data.status}</b></div>
            </div>
            <div className="text-xs text-slate-500">
              {data.counts?.audit_rows} audit rows · {data.counts?.otp_rows} OTP records
            </div>
          </div>

          {/* Chain nodes */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h3 className="font-bold text-slate-800 mb-3">Chain of Custody</h3>
            <ol className="relative border-l-2 border-slate-100 ml-2">
              {data.chain.map((n, i) => (
                <li key={i} className="ml-5 pb-4 last:pb-0" data-testid={`coc-node-${i}`}>
                  <span className="absolute -left-[9px] flex items-center justify-center">
                    {n.completed ? <CheckCircle2 className="h-4 w-4 text-emerald-500 bg-white" /> : <Circle className="h-4 w-4 text-slate-300 bg-white" />}
                  </span>
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className={`font-semibold text-sm ${n.completed ? "text-slate-800" : "text-slate-400"}`}>{n.stage}</span>
                    <span className="text-[11px] text-slate-400">{fmt(n.timestamp)}</span>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {n.actor_name ? `By: ${n.actor_name} ` : ""}<span className="text-slate-400">({n.actor_role})</span>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          {/* OTP audit trail */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2"><KeyRound className="h-4 w-4" style={{ color: ORANGE }} /> OTP Audit Trail</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500 text-left"><tr>
                  <th className="py-1 pr-3">Type</th><th className="pr-3">Generated by</th><th className="pr-3">Verified by</th><th className="pr-3">Verified at</th><th>Hash (no plaintext)</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.otp_log.map((o, i) => (
                    <tr key={i} data-testid={`coc-otp-${o.otp_type}`}>
                      <td className="py-1.5 pr-3 font-semibold">{o.otp_type}</td>
                      <td className="pr-3 font-mono text-[10px]">{o.generated_by || "—"}</td>
                      <td className="pr-3 font-mono text-[10px]">{o.verified_by || "—"}</td>
                      <td className="pr-3">{o.verified ? <span className="text-emerald-600 font-semibold">✓ {fmt(o.verified_at)}</span> : "pending"}</td>
                      <td className="font-mono text-[10px] text-slate-400 truncate max-w-[160px]">{(o.otp_hash || "").slice(0, 16)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Proof photos */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2"><Camera className="h-4 w-4" style={{ color: ORANGE }} /> Proof Photos</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <ProofImg label="Pickup proof" src={data.proofs.pickup_proof_photo} at={data.proofs.pickup_proof_at} fmt={fmt} />
              <ProofImg label={`Delivery proof${data.proofs.recipient_name ? ` · ${data.proofs.recipient_name}` : ""}`} src={data.proofs.delivery_proof_photo} at={data.proofs.delivery_proof_at} fmt={fmt} />
              <ProofImg label="Customer package photo" src={data.proofs.customer_package_photo} fmt={fmt} />
            </div>
          </div>

          {/* Line-haul */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2"><Truck className="h-4 w-4" style={{ color: ORANGE }} /> Line-haul (Delhivery · staff-only)</h3>
            <div className="text-xs text-slate-600 grid grid-cols-2 gap-y-1">
              <div>Courier: <b>{data.linehaul.courier || "—"}</b></div>
              <div>AWB: <b className="font-mono">{data.linehaul.awb || "—"}</b></div>
              <div>LRN: <b className="font-mono">{data.linehaul.lrn || "—"}</b></div>
              <div>Dispatched: {fmt(data.linehaul.dispatched_at)}</div>
              <div>Received: {fmt(data.linehaul.received_at)}</div>
            </div>
          </div>

          {/* Audit log */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2"><ShieldCheck className="h-4 w-4" style={{ color: ORANGE }} /> Audit Log ({data.audit_log.length})</h3>
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500 text-left sticky top-0 bg-white"><tr>
                  <th className="py-1 pr-3">Event</th><th className="pr-3">Actor</th><th className="pr-3">Role</th><th>Time</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.audit_log.map((a, i) => (
                    <tr key={i} data-testid={`coc-audit-${i}`}>
                      <td className="py-1.5 pr-3 font-semibold">{a.event_type}</td>
                      <td className="pr-3 font-mono text-[10px]">{a.actor_user_id || "—"}</td>
                      <td className="pr-3">{a.actor_role || "—"}</td>
                      <td className="text-slate-400">{fmt(a.at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProofImg({ label, src, at, fmt }) {
  return (
    <div className="border border-slate-100 rounded-xl p-2">
      <div className="text-[11px] font-semibold text-slate-600 mb-1">{label}</div>
      {src ? (
        <img src={src} alt={label} className="w-full h-24 object-cover rounded-lg border" />
      ) : (
        <div className="w-full h-24 rounded-lg bg-slate-50 border border-dashed flex items-center justify-center text-[10px] text-slate-400">No photo</div>
      )}
      {at && <div className="text-[10px] text-slate-400 mt-1">{fmt(at)}</div>}
    </div>
  );
}
